import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../../api/apiClient';
import { GET_DISTRIBUTORS_URL } from '../../../api/distributor';
import Loader from '../../../components/shared/Loader';
import EmptyState from '../../../components/shared/EmptyState';

/**
 * DistributorPickerModal
 * Shows a searchable list of active (status=1, is_delete=0) distributors.
 * The user picks one, then hits "Export Template" to proceed.
 *
 * Props:
 *   isOpen         — boolean
 *   onClose        — () => void
 *   onConfirm      — (distributor) => void
 */
function DistributorPickerModal({ isOpen, onClose, onConfirm }) {
  const [distributors, setDistributors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const searchRef = useRef(null);

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setSelected(null);
      setSearchQuery('');
      fetchDistributors();
      setTimeout(() => searchRef.current && searchRef.current.focus(), 120);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const fetchDistributors = async () => {
    try {
      setIsLoading(true);
      // Fetch up to 500 active, non-deleted distributors
      const res = await apiClient(`${GET_DISTRIBUTORS_URL}?page=1&limit=500`);
      const all = res?.distributors || [];
      // Only status=Active (BE already filters is_deleted=0)
      setDistributors(all.filter(d => d.status === 'Active'));
    } catch (err) {
      console.error('Failed to fetch distributors:', err);
      setDistributors([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = distributors.filter(d => {
    const q = searchQuery.toLowerCase();
    return (
      !q ||
      (d.email || '').toLowerCase().includes(q) ||
      (d.company || '').toLowerCase().includes(q) ||
      (d.distributor_code || '').toLowerCase().includes(q)
    );
  });

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1050,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
        backdropFilter: 'blur(4px)'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Select Distributor for Template Export"
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '520px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          animation: 'modalSlideIn 0.2s ease'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Icon */}
            <div style={{
              width: '42px', height: '42px', borderRadius: '10px',
              background: 'linear-gradient(135deg, #0b66e3 0%, #3b82f6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                Select Distributor
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                Choose a distributor to export their active songs template
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: '#f1f5f9', border: 'none', borderRadius: '8px',
              width: '32px', height: '32px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#64748b', flexShrink: 0, transition: 'background 0.15s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
            onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ position: 'relative' }}>
            <svg
              width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8"
              strokeWidth="2.5"
              style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by company or email..."
              style={{
                width: '100%',
                padding: '9px 12px 9px 36px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#0f172a',
                outline: 'none',
                boxSizing: 'border-box',
                background: '#f8fafc',
                transition: 'border-color 0.15s, box-shadow 0.15s'
              }}
              onFocus={e => {
                e.target.style.borderColor = '#0b66e3';
                e.target.style.boxShadow = '0 0 0 3px rgba(11,102,227,0.1)';
              }}
              onBlur={e => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {isLoading ? (
            <div style={{ padding: '32px 24px' }}>
              <Loader label="Loading distributors..." />
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '32px 24px' }}>
              <EmptyState
                title="No Distributors Found"
                subtitle={searchQuery ? 'Try a different search term.' : 'No active distributors available.'}
                hasSearch={!!searchQuery}
              />
            </div>
          ) : (
            filtered.map(dist => {
              const isSelected = selected?.id === dist.id;
              return (
                <button
                  key={dist.id}
                  type="button"
                  onClick={() => setSelected(dist)}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    border: 'none',
                    background: isSelected ? '#eff6ff' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    textAlign: 'left',
                    transition: 'background 0.12s',
                    borderLeft: isSelected ? '3px solid #0b66e3' : '3px solid transparent'
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '50%',
                    background: isSelected ? '#dbeafe' : '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: '14px', fontWeight: '700',
                    color: isSelected ? '#0b66e3' : '#64748b'
                  }}>
                    {(dist.company || dist.email || 'D').charAt(0).toUpperCase()}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px', fontWeight: '600',
                      color: isSelected ? '#0b66e3' : '#0f172a',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                      {dist.company || dist.email}
                    </div>
                    <div style={{
                      fontSize: '12px', color: '#64748b',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                      {dist.email}
                      {dist.songCount > 0 && (
                        <span style={{ marginLeft: '8px', color: '#94a3b8' }}>
                          · {dist.songCount} song{dist.songCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Check */}
                  {isSelected && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0b66e3" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          background: '#f8fafc'
        }}>
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>
            {filtered.length} distributor{filtered.length !== 1 ? 's' : ''} available
          </span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '9px 18px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                fontSize: '13px',
                fontWeight: '600',
                color: '#475569',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selected}
              onClick={() => selected && onConfirm(selected)}
              style={{
                padding: '9px 20px',
                borderRadius: '8px',
                border: 'none',
                background: selected ? 'linear-gradient(135deg, #0b66e3 0%, #3b82f6 100%)' : '#cbd5e1',
                fontSize: '13px',
                fontWeight: '600',
                color: '#ffffff',
                cursor: selected ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: selected ? '0 2px 8px rgba(11,102,227,0.3)' : 'none'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Export Template
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from { opacity: 0; transform: translateY(-12px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

export default DistributorPickerModal;
